import React, { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { PageHero } from "../components/PageHero";
import { CTABand } from "../components/CTABand";
import { Reveal } from "../components/Reveal";
import { WordReveal } from "../components/WordReveal";
import { ParallaxImage } from "../components/ParallaxImage";
import { WhatsAppLine } from "../components/WhatsAppLine";
import { Link } from "react-router-dom";
import { ArrowRightIcon } from "lucide-react";
import { MapleLeaf } from "../components/MapleLeaf";
import { IMAGES } from "../data/images";
import { RESIDENCES } from "../data/residences";
const PILLARS = [{
  title: "Community",
  copy: "Students from different social, cultural, religious and racial backgrounds form life-long friendships."
}, {
  title: "Supervision",
  copy: "Comfortable residences with trained educators and guardians watching out for every student."
}, {
  title: "Independence",
  copy: "Students grow into responsible, independent young adults, ready for multicultural universities."
}, {
  title: "Time back",
  copy: "No daily traffic. For working parents, the morning commute simply disappears."
}];
function ExpandingImage() {
  const ref = useRef<HTMLDivElement>(null);
  const reduce = useReducedMotion();
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ["start end", "center center"]
  });
  const clipPath = useTransform(scrollYProgress, [0, 1], reduce ? ["inset(0% 0% 0% 0%)", "inset(0% 0% 0% 0%)"] : ["inset(14% 18% 14% 18%)", "inset(0% 0% 0% 0%)"]);
  const scale = useTransform(scrollYProgress, [0, 1], reduce ? [1, 1] : [1.25, 1]);
  return <div ref={ref} className="relative h-[90vh] min-h-[520px]" data-id="element-721" data-mp="26v51qfbczkzb">
      <motion.div style={{
      clipPath
    }} className="absolute inset-0 overflow-hidden" data-id="element-722" data-mp="2br3mzu1e4mvi">
        <motion.img style={{
        scale
      }} src={IMAGES.dining} alt="Boarding students sharing a meal together" loading="lazy" className="h-full w-full object-cover" data-id="element-723" data-mp="2fui3elt8r59h" />
        <div className="absolute inset-0 bg-[#061f38]/35" data-id="element-724" data-mp="2kqgz6ugwfpjx" />
        <div className="absolute inset-x-0 bottom-0 mx-auto max-w-[1440px] px-5 pb-12 text-white md:px-9" data-id="element-725" data-mp="kr3j8k2oxrk5">
          <WordReveal text="Meals, study and friendship, under one roof." className="max-w-2xl text-4xl font-medium leading-[0.95] tracking-[-0.06em] md:text-6xl" data-id="element-726" data-mp="gqjk5ayk4dvd" />
        </div>
      </motion.div>
    </div>;
}
export function Boarding() {
  return <>
      <PageHero variant="full" eyebrow="BOARDING" title="A second home." description="Residences designed as a home away from home, and peace of mind for parents." image={IMAGES.boarding} imageAlt="Students in a warm residence lounge" breadcrumb="Boarding" data-id="element-727" data-mp="14drsk1ra831m" />

      <main data-id="element-728" data-mp="1dcvct926qohp">
        <section className="mx-auto max-w-[1440px] px-5 py-28 md:px-9 md:py-36" data-id="element-729" data-mp="18g4tmcv33cs2">
          <p className="text-xs font-semibold tracking-[0.18em] text-[#bb1e35]" data-id="element-730" data-mp="18ebnqzsaowl4">THE BOARDING ADVANTAGE</p>
          <WordReveal as="p" text="Where you live plays an important role in how you learn." className="mt-8 max-w-4xl text-4xl font-medium leading-[1.05] tracking-[-0.055em] md:text-6xl" data-id="element-731" data-mp="3f8fdl5mrlyle" />
        </section>

        <ExpandingImage data-id="element-732" data-mp="1uok0npms4tmw" />

        <section className="mx-auto grid max-w-[1440px] gap-12 px-5 py-28 md:px-9 lg:grid-cols-12 lg:py-36" data-id="element-733" data-mp="1uotkevh8e4k5">
          <div className="lg:col-span-4" data-id="element-734" data-mp="1gw3y0pxsixmi">
            <div className="lg:sticky lg:top-32" data-id="element-735" data-mp="1gvcbe4m68a8d">
              <p className="text-xs font-semibold tracking-[0.18em] text-[#bb1e35]" data-id="element-736" data-mp="1kxp4ntjqefo9">IS MY CHILD SAFE?</p>
              <h2 className="mt-5 text-4xl font-medium leading-[0.95] tracking-[-0.06em] md:text-5xl" data-id="element-737" data-mp="16xxy2dvjo1ka">What boarding gives your family.</h2>
            </div>
          </div>
          <div className="lg:col-span-7 lg:col-start-6" data-id="element-738" data-mp="xysx70wifw4y">
            {PILLARS.map((p, i) => <Reveal key={p.title} className="grid gap-4 border-t border-[#dedbd3] py-10 md:grid-cols-[100px_1fr]" data-id="element-739" data-mp="11zmo83591r22" data-mpi={i}>
                <span className="text-sm font-semibold text-[#bb1e35]" data-id="element-740" data-mp="2sc2az4w3a3t0">0{i + 1}</span>
                <div data-id="element-741" data-mp="2wefdzljgbnm3">
                  <h3 className="text-3xl font-medium tracking-[-0.05em] md:text-4xl" data-id="element-742" data-mp="2wcvvdoabp0h5">{p.title}</h3>
                  <p className="mt-3 max-w-md text-base leading-7 text-[#4f5963]" data-id="element-743" data-mp="27m81e714kds6">{p.copy}</p>
                </div>
              </Reveal>)}
          </div>
        </section>

        <section className="bg-[#f2f0eb] py-24 md:py-32" data-id="element-744" data-mp="1xoag9sekax3s">
          <div className="mx-auto max-w-[1440px] px-5 md:px-9" data-id="element-745" data-mp="1xqwq5yoxdop6">
            <Reveal className="mb-14 flex flex-col justify-between gap-4 md:flex-row md:items-end" data-id="element-746" data-mp="1sph2rzz9z63m">
              <div data-id="element-747" data-mp="1opyj17j6x12d">
                <p className="text-xs font-semibold tracking-[0.18em] text-[#bb1e35]" data-id="element-748" data-mp="1oqi2wtsehzj1">THE RESIDENCES</p>
                <h2 className="mt-5 text-4xl font-medium tracking-[-0.06em] md:text-6xl" data-id="element-749" data-mp="1jvlzyylmlrr5">Two hostels. One community.</h2>
              </div>
              <p className="max-w-xs text-sm text-[#4f5963]" data-id="element-750" data-mp="1a0ag61vd1ucj">Separate residences for girls and boys. Open either to see inside.</p>
            </Reveal>
            <div className="grid gap-10 md:grid-cols-2" data-id="element-751" data-mp="152ion8ymoa8v">
              {[RESIDENCES.girls, RESIDENCES.boys].map((r, i) => <div key={r.key} className={i === 1 ? "md:mt-24" : ""} data-id="element-752" data-mp="3cmg4xo73lxzh" data-mpi={i}>
                  <Link to={`/boarding/residences/${r.key}`} className="group block focus:outline-none focus-visible:ring-2 focus-visible:ring-[#bb1e35] focus-visible:ring-offset-4" aria-label={`View the ${r.name} gallery`} data-id="element-753" data-mp="3co8zi54fsrr4">
                    <div className="overflow-hidden" data-id="element-754" data-mp="3qlp91pi151n3">
                      <div className="transition-transform duration-700 ease-out group-hover:scale-[1.03]" data-id="element-755" data-mp="3qlfj21wziuhe">
                        <img src={r.cover} alt={`Inside the ${r.name}`} width={800} height={1000} className="aspect-[4/5] w-full bg-[#dedbd3] object-cover" data-id="element-756" data-mp="2t008vzijkttq" />
                      </div>
                    </div>
                  </Link>
                  <Reveal className="mt-5 border-b border-[#cfcac0] pb-5" data-id="element-757" data-mp="37mtg0hhwqtau">
                    <div className="flex items-baseline justify-between gap-4" data-id="element-758" data-mp="37p63fa6fpvva">
                      <h3 className="text-2xl font-medium tracking-[-0.04em]" data-id="element-759" data-mp="2a7fjis21x6uo">{r.name}</h3>
                      <span className="text-xs text-[#6b7378]" data-id="element-760" data-mp="2f57lyg0bobd4">{r.photos.length} photos</span>
                    </div>
                    <Link to={`/boarding/residences/${r.key}`} className="group mt-5 inline-flex items-center gap-2 bg-[#061f38] px-4 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#0c2c4d]" data-id="element-761" data-mp="273zzot6nub1e">
                      <MapleLeaf size={12} className="text-[#f0b1b9]" data-id="element-762" data-mp="2bwt7jpxfdzhp" /> View {r.short} gallery <ArrowRightIcon size={15} className="transition-transform duration-200 group-hover:translate-x-1" data-id="element-763" data-mp="2byw3srp68ouv" />
                    </Link>
                  </Reveal>
                </div>)}
            </div>
            <Reveal className="mt-16 grid gap-6 border-t border-[#061f38] pt-8 md:grid-cols-2" data-id="element-764" data-mp="1y54tvi7j32ym">
              <p className="max-w-md text-base leading-7 text-[#3d4b57]" data-id="element-765" data-mp="1t6l5lb5gpf33">Boarding students resume a few days before classes begin, giving them time to settle into their new living environment.</p>
              <div className="md:justify-self-end" data-id="element-766" data-mp="34lgudpxtowxd">
                <WhatsAppLine label="Ask about boarding availability" context="boarding at MCC, including availability and residence arrangements" data-id="element-767" data-mp="34kypy610bced" />
              </div>
            </Reveal>
          </div>
        </section>
      </main>

      <CTABand title="Visit the residences before you decide." image={IMAGES.boarding} data-id="element-768" data-mp="vafuotns0ven" />
    </>;
}